import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, ArrowLeft, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import jm from 'jalali-moment';
import { bookServiceApi } from '@/services/api';
import { useGoalModal } from '@/features/profile/contexts/GoalModalContext';
import { getMe } from '@/services/authService';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import FeatureGuideOverlay from '@/components/ui/FeatureGuideOverlay';

// Persian digits helper (keep UI digits Persian without altering moment internal)

const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

const toPersianNumber = (num: number | string): string => {
  return num.toString().replace(/\d/g, (digit) => persianNumbers[parseInt(digit)]);
};

const STREAK_CALENDAR_GUIDE_STEPS = [
  {
    id: 'streak-calendar-overview',
    title: 'تقویم پیوستگی',
    description: 'اینجا می‌تونی روزهایی که کتاب خوندی و اطلاعات پیوستگی خودت رو ببینی.',
  },
];

// Helper: Persian digits

// (Optional) hook up real streak stats later via API

const StreakCalendarPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openStreakGoalModal } = useGoalModal();
  const [currentStreak, setCurrentStreak] = useState(0);
  const [daysUntilLongest, setDaysUntilLongest] = useState(0);
  const [streakGoal, setStreakGoal] = useState<number>(30);
  const [loadingGoal, setLoadingGoal] = useState(true);

  // Current visible month in Jalali
  const [viewDate, setViewDate] = useState(() => jm().locale('fa'));

  // Highlighted days (from server) as a set of keys: `${y}-${m}-${d}` in Jalali
  const [highlightedSet, setHighlightedSet] = useState<Set<string>>(new Set());
  // Freeze dates (streak freeze used)
  const [freezeSet, setFreezeSet] = useState<Set<string>>(new Set());

  const daysGrid = useMemo(() => {
    // Start of current jMonth
    const start = viewDate.clone().startOf('jMonth');
    const end = viewDate.clone().endOf('jMonth');

    // Weekday index: 0=Sunday ... 6=Saturday in moment. For Persian weeks (Sat-Fri), we arrange columns RTL.
    const startWeekday = parseInt(start.format('d')); // 0..6 (Sun..Sat)
    // We want columns order: Sat(6), Sun(0), Mon(1), Tue(2), Wed(3), Thu(4), Fri(5)
    // Number of leading blanks before first day when grid header starts at Saturday
    const weekdayOrder = [6, 0, 1, 2, 3, 4, 5];
    const blanksBefore = weekdayOrder.indexOf(startWeekday);

    const days: Array<{ year: number; month: number; day: number; isWeekend: boolean } | null> = [];
    for (let i = 0; i < blanksBefore; i++) days.push(null);

    const cursor = start.clone();
    while (cursor.isSameOrBefore(end)) {
      const weekday = parseInt(cursor.format('d'));
      const jYear = parseInt(cursor.format('jYYYY'));
      const jMonth = parseInt(cursor.format('jM'));
      const jDay = parseInt(cursor.format('jD'));
      const isWeekend = weekday === 5 || weekday === 6; // Fri(5), Sat(6)
      days.push({ year: jYear, month: jMonth, day: jDay, isWeekend });
      cursor.add(1, 'day');
    }

    // Pad to complete last week row
    while (days.length % 7 !== 0) days.push(null);

    // Chunk into weeks
    const weeks: typeof days[] = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return { weeks, monthLabel: toPersianNumber(viewDate.format('jMMMM jYYYY')) };
  }, [viewDate]);

  const todayJ = useMemo(() => {
    const t = jm().locale('fa');
    return {
      year: parseInt(t.format('jYYYY')),
      month: parseInt(t.format('jM')),
      day: parseInt(t.format('jD')),
    };
  }, []);

  // Fetch streak dates from server using shared ApiClient with auth/refresh
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const res = await bookServiceApi.get<{
          dates: string[];
          freeze_dates: string[];
          max_streak?: number;
          current_streak?: number;
          distance_to_record?: number;
        }>(`/completed-sections/dates`);

        if (!res.success || !res.data) {
          throw new Error(res.error || 'Failed to load streak data');
        }

        const data = res.data;

        // Bind counters
        if (typeof data.current_streak === 'number') setCurrentStreak(data.current_streak);
        if (typeof data.distance_to_record === 'number') setDaysUntilLongest(data.distance_to_record);

        // Helper to parse dates
        const parseToSet = (dateList: string[] = []) => {
          const s = new Set<string>();
          for (const d of dateList) {
            const [jy, jmth, jd] = d.split('-').map((x) => parseInt(x, 10));
            if (!Number.isNaN(jy) && !Number.isNaN(jmth) && !Number.isNaN(jd)) {
              s.add(`${jy}-${jmth}-${jd}`);
            }
          }
          return s;
        };

        setHighlightedSet(parseToSet(data.dates));
        setFreezeSet(parseToSet(data.freeze_dates));
      } catch (e) {
        // Optionally add user-facing toast/log here
        console.error('Failed to fetch streak dates', e);
      }
    };
    fetchDates();
  }, []);

  // Fetch streak goal from profile
  useEffect(() => {
    const fetchGoal = async () => {
      try {
        const profile = await getMe();
        if (typeof profile.streak_goal === 'number') {
          setStreakGoal(profile.streak_goal);
        }
      } catch (e) {
        console.error('Failed to fetch streak goal', e);
      } finally {
        setLoadingGoal(false);
      }
    };
    fetchGoal();
  }, []);

  // Listen for goal updates
  useEffect(() => {
    const handleGoalUpdate = async () => {
      try {
        const profile = await getMe();
        if (typeof profile.streak_goal === 'number') {
          setStreakGoal(profile.streak_goal);
        }
      } catch (e) {
        console.error('Failed to reload streak goal', e);
      }
    };

    window.addEventListener('goalUpdated', handleGoalUpdate);
    return () => window.removeEventListener('goalUpdated', handleGoalUpdate);
  }, []);

  const prevMonth = () => setViewDate((v) => v.clone().subtract(1, 'jMonth'));
  const nextMonth = () => setViewDate((v) => v.clone().add(1, 'jMonth'));

  return (
    <div className="min-h-screen bg-[#fafafa] pb-[calc(6rem+env(safe-area-inset-bottom))]" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-400 to-orange-500 text-white pt-safe-top">
        <div className="container mx-auto px-4 py-4 max-w-2xl lg:max-w-3xl">
          <div className="flex items-center justify-between mb-6 mt-0">
            <button
              onClick={() => navigate(-1)}
              className="relative -top-1.5 flex h-8 w-8 items-center justify-center hover:opacity-70 hover:scale-110 transition-all duration-200"
              aria-label="بازگشت"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>

            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold font-persian">پیوستگی</h1>

            <div className="w-5" />
          </div>

          {/* Streak Display */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-6xl md:text-7xl lg:text-8xl font-bold mb-2">
                {toPersianNumber(currentStreak)}
              </div>
              <div className="text-lg md:text-xl lg:text-2xl font-persian">
                پیوستگی شما!
              </div>
            </div>
            <div className="relative">
              <motion.div
                animate={{
                  filter: [
                    'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))',
                    'drop-shadow(0 0 30px rgba(255, 255, 255, 0.5))',
                    'drop-shadow(0 0 20px rgba(255, 255, 255, 0.3))'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <Flame className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 text-white" fill="currentColor" />
              </motion.div>
            </div>
          </div>

          {/* Progress Message */}
          {daysUntilLongest > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-800/80 rounded-2xl p-4 md:p-5 lg:p-6 mt-6 flex items-center gap-3"
            >
              <Flame className="w-8 h-8 md:w-10 md:h-10 text-orange-400" fill="currentColor" />
              <p className="text-white font-persian leading-relaxed text-sm md:text-base">
                شما {toPersianNumber(daysUntilLongest)} روز تا رسیدن به بیشترین میزان رکورد خود فاصله دارید!
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Calendar Section */}
      <div className="bg-[#fafafa] text-gray-800">
        <div className="container mx-auto px-4 py-6 max-w-2xl lg:max-w-3xl">
          {/* Inline Jalali Calendar (no third-party UI component) */}
          <div className="bg-white rounded-2xl px-4 pt-4 pb-3 md:px-6 md:pt-6 md:pb-4 shadow-sm border border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <button onClick={nextMonth} className="px-3 py-1 md:px-4 md:py-2 rounded-lg hover:bg-gray-100 hover:scale-105 transition-all duration-200" aria-label="ماه بعد">
                <ArrowRight className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <div className="font-bold font-persian text-base md:text-lg lg:text-xl">{daysGrid.monthLabel}</div>
              <button onClick={prevMonth} className="px-3 py-1 md:px-4 md:py-2 rounded-lg hover:bg-gray-100 hover:scale-105 transition-all duration-200" aria-label="ماه قبل">
                <ArrowLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>
            </div>

            {/* Weekday Header (Sat..Fri) */}
            <div className="grid grid-cols-7 gap-1 md:gap-2 text-center text-sm md:text-base text-gray-600 mb-2">
              {['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'].map((w) => (
                <div key={w} className="py-1 md:py-2">{w}</div>
              ))}
            </div>

            {/* Days */}
            <div className="grid grid-cols-7 place-items-center gap-0 text-center font-persian !leading-none auto-rows-[36px] md:auto-rows-[48px] lg:auto-rows-[56px]">
              {daysGrid.weeks.map((week, wi) => (
                <React.Fragment key={wi}>
                  {week.map((cell, ci) => {
                    if (!cell) return <div key={ci} className="py-2" />;
                    const base = 'flex items-center justify-center !leading-none select-none cursor-default text-sm md:text-base lg:text-lg';
                    const isToday =
                      cell.year === todayJ.year &&
                      cell.month === todayJ.month &&
                      cell.day === todayJ.day;
                    const isFuture =
                      cell.year > todayJ.year ||
                      (cell.year === todayJ.year && cell.month > todayJ.month) ||
                      (cell.year === todayJ.year && cell.month === todayJ.month && cell.day > todayJ.day);
                    const key = `${cell.year}-${cell.month}-${cell.day}`;
                    const isMarked = highlightedSet.has(key);
                    const isFreezed = freezeSet.has(key);
                    const previousCell = ci > 0 ? week[ci - 1] : null;
                    const nextCell = ci < week.length - 1 ? week[ci + 1] : null;
                    const previousKey = previousCell
                      ? `${previousCell.year}-${previousCell.month}-${previousCell.day}`
                      : '';
                    const nextKey = nextCell
                      ? `${nextCell.year}-${nextCell.month}-${nextCell.day}`
                      : '';
                    const isPreviousMarked = isMarked && highlightedSet.has(previousKey);
                    const isNextMarked = isMarked && highlightedSet.has(nextKey);
                    const isPartOfPill = isPreviousMarked || isNextMarked;
                    const sizeClass = isMarked && isPartOfPill
                      ? 'w-full h-[28px] md:h-[36px] lg:h-[44px]'
                      : 'h-[28px] w-[28px] md:h-[36px] md:w-[36px] lg:h-[44px] lg:w-[44px]';
                    const pillShape = isMarked
                      ? `${isPreviousMarked ? '' : 'rounded-r-full'} ${isNextMarked ? '' : 'rounded-l-full'}`
                      : 'rounded-full';

                    const color = isFuture ? 'text-gray-300' : 'text-gray-800';
                    // If marked or freezed, confirm text is white for contrast
                    const textOverride = (isMarked || isFreezed) && !isFuture ? 'text-white' : color;

                    // Background classes
                    let bgClass = '';
                    if (!isFuture) {
                      if (isMarked) bgClass = 'bg-orange-400';
                      else if (isFreezed) bgClass = 'bg-cyan-500'; // Cold/Icy blue
                    }

                    return (
                      <div
                        key={ci}
                        className={`${base} ${sizeClass} ${pillShape} ${textOverride} ${isToday && !isMarked && !isFreezed ? 'ring-2 ring-orange-400' : ''} ${bgClass}`}
                        style={{ transform: 'translateY(-3px)' }}
                      >
                        {toPersianNumber(cell.day)}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Streak Goal Section */}
          <div className="mt-6">
            <button
              onClick={openStreakGoalModal}
              disabled={loadingGoal}
              className="w-full flex items-center justify-between p-4 bg-white rounded-3xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-500 group-hover:scale-110 transition-transform">
                  <Flame className="w-5 h-5" />
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900 font-persian text-sm">هدف پیوستگی</div>
                  {loadingGoal ? (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  ) : (
                    <div className="text-xs text-muted font-persian mt-0.5">{toPersianNumber(streakGoal)} روز</div>
                  )}
                </div>
              </div>
              <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      {!loadingGoal && user?.is_registered && !user.is_guest && (
        <FeatureGuideOverlay
          steps={STREAK_CALENDAR_GUIDE_STEPS}
        />
      )}
    </div>
  );
}

export default StreakCalendarPage;
